// Stripe.js i kassan. Skriptet laddas från js.stripe.com först när kunden
// väljer hemleverans (Stripe kräver att det laddas därifrån, se CSP i
// vercel.json). Kortuppgifter och Swish/Klarna hanteras helt i Stripes
// eget fält; de passerar aldrig vår server.

export interface StripePaymentResult {
    error?: { message?: string; type?: string };
    paymentIntent?: { id: string; status: string };
}

export interface StripeElement {
    mount: (el: HTMLElement) => void;
    unmount: () => void;
    on: (event: 'ready' | 'change', handler: (e: any) => void) => void;
}

export interface StripeElements {
    create: (type: 'payment', options?: Record<string, unknown>) => StripeElement;
    submit: () => Promise<{ error?: { message?: string } }>;
}

export interface StripeInstance {
    elements: (options: Record<string, unknown>) => StripeElements;
    confirmPayment: (options: {
        elements: StripeElements;
        confirmParams: { return_url: string; receipt_email?: string };
        redirect: 'if_required';
    }) => Promise<StripePaymentResult>;
    retrievePaymentIntent: (clientSecret: string) => Promise<StripePaymentResult>;
}

declare global {
    interface Window {
        Stripe?: (key: string, options?: Record<string, unknown>) => StripeInstance;
    }
}

const PUBLISHABLE_KEY = (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined) || '';

/** Hemleverans kan bara erbjudas när den publika nyckeln finns i miljön. */
export const stripeConfigured = (): boolean => /^pk_(test|live)_/.test(PUBLISHABLE_KEY);

/** Sandlådan: inga riktiga pengar dras. Visas i kassan så att ingen blir lurad under test. */
export const stripeTestMode = (): boolean => PUBLISHABLE_KEY.startsWith('pk_test_');

let stripePromise: Promise<StripeInstance> | null = null;

export const loadStripe = (): Promise<StripeInstance> => {
    if (stripePromise) return stripePromise;
    stripePromise = new Promise<StripeInstance>((resolve, reject) => {
        if (!stripeConfigured()) {
            reject(new Error('Betalning är inte konfigurerad.'));
            return;
        }
        const ready = () => {
            if (window.Stripe) resolve(window.Stripe(PUBLISHABLE_KEY, { locale: 'sv' }));
            else reject(new Error('Betalningen kunde inte laddas.'));
        };
        if (window.Stripe) return ready();
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.async = true;
        script.onload = ready;
        script.onerror = () => {
            stripePromise = null;
            reject(new Error('Betalningen kunde inte laddas. Kontrollera anslutningen och försök igen.'));
        };
        document.head.appendChild(script);
    });
    return stripePromise;
};

/** Stripes fält i samma ton som kassan: kaffebrunt, rundade hörn, systemtypsnitt. */
export const STRIPE_APPEARANCE = {
    theme: 'stripe',
    variables: {
        colorPrimary: '#816358',
        colorText: '#292524',
        colorTextSecondary: '#78716c',
        colorDanger: '#b91c1c',
        colorBackground: '#ffffff',
        borderRadius: '12px',
        fontFamily: '"Plus Jakarta Sans", system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
        spacingUnit: '4px',
    },
    rules: {
        '.Input': { border: '1px solid #e7e5e4', boxShadow: 'none' },
        '.Input:focus': { border: '1px solid #816358', boxShadow: '0 0 0 3px rgba(129, 99, 88, 0.15)' },
        '.Tab': { border: '1px solid #e7e5e4', boxShadow: 'none' },
        '.Tab--selected': { border: '1px solid #816358', boxShadow: '0 0 0 1px #816358' },
        '.Label': { fontWeight: '600', fontSize: '12px' },
    },
};

// --- Tillbaka från Swish/Klarna/3D Secure ---------------------------------
//
// Vissa betalsätt lämnar sidan och kommer tillbaka till return_url med
// ?payment_intent_client_secret=… i adressen. Kvittot och vilken butikssida
// kunden var på sparas i sessionStorage innan betalningen, så att tacksidan
// kan visas igen.

const RETURN_KEY = 'cn_checkout_return';

export interface PendingCheckout<R> {
    hash: string;
    receipt: R;
    savedAt: number;
}

export const savePendingCheckout = <R,>(receipt: R): void => {
    try {
        // Butikssidan utan ?cart=open, så att tacksidan visas i stället för varukorgen.
        const payload: PendingCheckout<R> = { hash: window.location.hash.split('?')[0] || '#/', receipt, savedAt: Date.now() };
        sessionStorage.setItem(RETURN_KEY, JSON.stringify(payload));
    } catch {
        // Privat läge: tacksidan efter en omdirigering blir enklare, betalningen påverkas inte.
    }
};

export const readPendingCheckout = <R,>(): PendingCheckout<R> | null => {
    try {
        const raw = sessionStorage.getItem(RETURN_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as PendingCheckout<R>;
        return Date.now() - parsed.savedAt < 6 * 60 * 60 * 1000 ? parsed : null;
    } catch {
        return null;
    }
};

export const clearPendingCheckout = (): void => {
    try { sessionStorage.removeItem(RETURN_KEY); } catch { /* ignoreras */ }
};

/** Adressen Stripe skickar tillbaka kunden till: sidans rot, utan #-delen. */
export const stripeReturnUrl = (): string => `${window.location.origin}${window.location.pathname}`;

/** Läser Stripes parametrar efter en omdirigering, eller null. */
export const stripeReturnParams = (): { clientSecret: string; redirectStatus: string | null } | null => {
    const params = new URLSearchParams(window.location.search);
    const clientSecret = params.get('payment_intent_client_secret');
    return clientSecret ? { clientSecret, redirectStatus: params.get('redirect_status') } : null;
};

/** Tar bort Stripes parametrar ur adressen och går tillbaka till butikssidan. */
export const restoreAfterStripeReturn = (hash: string): void => {
    window.history.replaceState(null, '', `${window.location.pathname}${hash || '#/'}`);
};
